import styled from '@emotion/styled';
import { Formik, FormikHelpers } from 'formik';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { announceNewTeam } from 'modules/teams/store/championship-teams/actions';
import { useLazyGetTeamByIdQuery } from 'modules/teams/store/teams-api';
import { TeamAsyncSelect, TeamSelectOption } from 'modules/teams/ui/components/team-async-select';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { trackEvent } from 'shared/analytics';

import { AppDispatch } from 'store/store';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { theme } from 'styles/theme/theme';

const AddTeamApplicationFormWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
`;

const AddTeamApplicationFormContainer = styled.div`
  padding: 16px 20px 24px 20px;
  background-color: ${theme.colors.SurfaceWhite};
  border-radius: 8px;
  box-shadow:
    0 0 1px color-mix(in srgb, ${theme.colors.SurfaceRaisinBlack50} 8%, transparent),
    0 8px 16px color-mix(in srgb, ${theme.colors.SurfaceRaisinBlack50} 8%, transparent);
  margin-bottom: 42px;
`;

const AddTeamApplicationFormFields = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;

  > div {
    margin: 0 5px;
    &:first-child {
      margin-left: 0;
    }
    &:last-child {
      margin-right: 0;
    }
  }
`;

type AddTeamApplicationFormProps = {
  championshipId: number;
  existTeamsId: number[];
};

const AddTeamApplicationForm = ({ championshipId }: AddTeamApplicationFormProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { info } = useOrganizer();
  const navigate = useNavigate();
  const [fetchTeam] = useLazyGetTeamByIdQuery();

  type FormValues = {
    team?: TeamSelectOption;
  };

  const handleSubmit = useCallback(
    async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
      const { team } = values;
      if (!team) {
        return;
      }

      if (team.__isNew__) {
        if (!info) {
          return;
        }

        // Создание команды прямо из формы заявки упразднено: команду заводят отдельно,
        // а сюда добавляют уже существующую.
        toast.warn('Функционал упразднён. Если вы увидели эту ошибку, пожалуйста, сообщите в поддержку.');
        throw Error('Functionality is disabled. If you see this error, please contact support.');
      } else {
        const detailTeam = await fetchTeam(team.data!.id).unwrap();

        if (!detailTeam) {
          toast.error('Команда не найдена');
          return;
        }

        await dispatch(
          announceNewTeam({
            team: {
              id: detailTeam.id,
              name: detailTeam.name,
              shortName: detailTeam.shortName,
              logo: detailTeam.logo,
              players: detailTeam.players.filter((player) => !player.isReal),
            },
            championshipId,
          }),
        ).unwrap();

        // Команда попала в «Незаявленные». В старом флоу за раз добавляют ровно одну.
        trackEvent('Team Added To Championship', { championship_id: championshipId, flow: 'v1', teams_count: 1 });

        resetForm();
        navigate(`${team.data!.id}`);
      }
    },
    [championshipId, dispatch, fetchTeam, info, navigate],
  );

  return (
    <AddTeamApplicationFormWrapper>
      <Formik<FormValues> initialValues={{ team: undefined }} onSubmit={handleSubmit}>
        {(formProps) => {
          return (
            <form onSubmit={formProps.handleSubmit}>
              <AddTeamApplicationFormContainer>
                <UDText type="subhead" className="bold">
                  Добавить новую команду
                </UDText>

                <AddTeamApplicationFormFields>
                  <TeamAsyncSelect
                    className="w-[90%]"
                    name="team"
                    value={formProps.values.team}
                    onChange={(option) => formProps.setFieldValue('team', option)}
                    onBlur={() => formProps.setFieldTouched('team', true)}
                  />
                  <div>
                    <UDButton
                      type="submit"
                      variant="primary"
                      loading={formProps.isSubmitting}
                      disabled={!formProps.values.team || !formProps.dirty}
                    >
                      Добавить
                    </UDButton>
                  </div>
                </AddTeamApplicationFormFields>
              </AddTeamApplicationFormContainer>
            </form>
          );
        }}
      </Formik>
    </AddTeamApplicationFormWrapper>
  );
};

export default AddTeamApplicationForm;
